/**
 * Centralized authentication state management (W1 S0a extraction from App.tsx).
 *
 * Moved verbatim out of App.tsx except: `logout` now purges the query cache via
 * `useQueryClient()` instead of closing over App.tsx's module-local `queryClient`
 * (RED-TEAM round 2 — the module-local instance is declared after this block and
 * does not move with it; closing over it would silently drop the RT-3 session-bleed
 * purge on logout).
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { STORAGE_KEYS, AUTH_CONFIG } from '../constants/config';
import { performLogout } from '@secureembed/shared-crm';
import { invalidateObservations } from '../scope/runtime';
import type { ObservationIdentity } from '../scope/types';

export interface AuthUser {
  sub?: string;
  email?: string;
  tenant_id?: string;
  role?: string;
  username?: string;
  groups?: string[];
}

export interface AuthContextType {
  observationIdentity: ObservationIdentity;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  login: (token: string, user?: AuthUser) => void;
  logout: () => void;
  checkAuth: () => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [observationIdentity, setObservationIdentity] = useState<ObservationIdentity>({ principalKey: '', sessionEpoch: '', permissionEpoch: '' });
  const synchroniseIdentity = useCallback(() => {
    invalidateObservations();
    void queryClient.cancelQueries(); queryClient.clear();
    try {
      const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || '';
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload.sub || !payload.iss || !payload.exp || payload.exp * 1000 <= Date.now()) throw new Error('No current identity');
      const epoch = crypto.randomUUID();
      setObservationIdentity({ principalKey: `${payload.iss}|${payload.sub}`, sessionEpoch: epoch, permissionEpoch: epoch });
      setUser(previous => ({ ...previous, sub: payload.sub, groups: Array.isArray(payload['cognito:groups']) ? payload['cognito:groups'] : [] }));
    } catch { setObservationIdentity({ principalKey: '', sessionEpoch: '', permissionEpoch: '' }); }
  }, [queryClient]);

  const parseGroupsFromToken = useCallback((token: string): string[] => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return [];
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return Array.isArray(payload['cognito:groups']) ? payload['cognito:groups'] : [];
    } catch {
      return [];
    }
  }, []);

  // C2-partial: Check token expiry with proper base64url decoding
  const isTokenValid = useCallback((token: string): boolean => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        // For demo tokens or non-JWT, assume valid
        return token.startsWith('demo-token-') || token.length > 0;
      }
      // C2: Proper base64url decoding for JWT payload
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        // Token expired - clear all tokens
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }, []);

  // Check authentication status
  const checkAuth = useCallback((): boolean => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) {
      return false;
    }
    return isTokenValid(token);
  }, [isTokenValid]);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = () => {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER);

      if (token && isTokenValid(token)) {
        setIsAuthenticated(true);
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser) as AuthUser;
            if (!parsed.groups) parsed.groups = parseGroupsFromToken(token);
            setUser(parsed);
          } catch {
            setUser({ email: AUTH_CONFIG.DEFAULT_USER_EMAIL, groups: parseGroupsFromToken(token) });
          }
        }
      } else {
        // Clear invalid tokens
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        setIsAuthenticated(false);
        setUser(null);
      }
      setIsLoading(false);
    };

    initAuth();
    synchroniseIdentity();
    let previousToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const refreshIdentity = () => {
      const currentToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (currentToken !== previousToken) { previousToken = currentToken; initAuth(); synchroniseIdentity(); }
    };
    window.addEventListener('storage', refreshIdentity);
    window.addEventListener('focus', refreshIdentity);

    // Re-check auth periodically
    const interval = setInterval(() => {
      refreshIdentity();
      if (!checkAuth()) {
        invalidateObservations(); queryClient.clear();
        setIsAuthenticated(false);
        setUser(null);
      }
    }, AUTH_CONFIG.CHECK_INTERVAL_MS);

    return () => { clearInterval(interval); window.removeEventListener('storage', refreshIdentity); window.removeEventListener('focus', refreshIdentity); };
  }, [isTokenValid, checkAuth, synchroniseIdentity, queryClient]);

  const login = useCallback((token: string, userData?: AuthUser) => {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    const enriched: AuthUser = {
      ...userData,
      groups: userData?.groups ?? parseGroupsFromToken(token),
    };
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(enriched));
    setUser(enriched);
    setIsAuthenticated(true);
    synchroniseIdentity();
  }, [parseGroupsFromToken, synchroniseIdentity]);

  const logout = useCallback(() => {
    invalidateObservations();
    performLogout({
      storageKeys: [
        STORAGE_KEYS.AUTH_TOKEN,
        STORAGE_KEYS.ID_TOKEN,
        STORAGE_KEYS.REFRESH_TOKEN,
        STORAGE_KEYS.USER,
      ],
      onClear: () => {
        // RT-3: drop all cached query data on logout so the next user in this tab
        // never sees the previous user's data (session-bleed hardening, #2256).
        queryClient.clear();
        setIsAuthenticated(false);
        setUser(null);
      },
    });
  }, [queryClient]);

  return (
    <AuthContext.Provider value={{ observationIdentity, isAuthenticated, isLoading, user, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
